// src/pages/logistics/PageDeliveryPlan.jsx
import { useState, useEffect, useMemo } from "react";
import * as api from "../../apiService";
import { useToast } from "../../components/Toast";

const REGIONS = ["Abu Dhabi", "Dubai", "Sharjah", "Ajman", "Fujairah", "RAK", "UAQ", "Other"];

function tomorrow() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

function fmt(d) {
  if (!d) return "—";
  try {
    const [y, m, day] = d.slice(0, 10).split("-");
    return `${day}/${m}/${y}`;
  } catch { return d; }
}

function dueSoon(dateStr) {
  if (!dateStr) return false;
  return (new Date(dateStr) - new Date()) / 86400000 <= 3;
}

function StatusBadge({ status }) {
  if (status === "FINAL")
    return <span style={{ background: "#dcfce7", color: "#166534", borderRadius: 4, padding: "2px 8px", fontSize: 11, fontWeight: 700 }}>✓ Finalized</span>;
  return <span style={{ background: "#fefce8", color: "#854d0e", borderRadius: 4, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>Draft</span>;
}

// ── Add-to-Plan modal (region + vehicle picker) ───────────────────────────────

function AddToPlanModal({ lineData, existingLines, onConfirm, onClose }) {
  const [region,  setRegion]  = useState("Other");
  const [vehicle, setVehicle] = useState("");

  // Auto-fill vehicle for the selected region if one already exists
  useEffect(() => {
    const existing = existingLines.find(l => (l.region || "Other") === region && l.vehicle);
    setVehicle(existing?.vehicle ?? "");
  }, [region, existingLines]);

  return (
    <div className="overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: "white", borderRadius: 8, padding: 20, width: 320, boxShadow: "0 8px 32px rgba(0,0,0,0.15)" }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>Assign to Delivery Route</div>
        <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 14 }}>
          {lineData.partyName} — <span style={{ fontFamily: "monospace", fontWeight: 600 }}>{lineData.grade}</span>
        </div>
        <label className="form-label" style={{ fontSize: 11 }}>Region / Emirate</label>
        <select className="form-input" value={region} onChange={e => setRegion(e.target.value)} style={{ marginBottom: 10 }}>
          {REGIONS.map(r => <option key={r}>{r}</option>)}
        </select>
        <label className="form-label" style={{ fontSize: 11 }}>Vehicle / Driver</label>
        <input className="form-input" value={vehicle} onChange={e => setVehicle(e.target.value)}
          placeholder="e.g. Toyota Hilux — DXB 12345" style={{ marginBottom: 16 }} />
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="btn" onClick={onClose} style={{ fontSize: 12 }}>Cancel</button>
          <button className="btn btn-primary" onClick={() => onConfirm(region, vehicle)} style={{ fontSize: 12 }}>Add to Schedule</button>
        </div>
      </div>
    </div>
  );
}

// ── Open Orders (left column) ─────────────────────────────────────────────────

function OpenOrdersSection({ onRequestAdd }) {
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [search,  setSearch]  = useState("");

  useEffect(() => {
    setLoading(true);
    Promise.all([api.fetchOrderBook("AMI"), api.fetchOrderBook("AMT")])
      .then(([ami, amt]) => {
        const flat = [];
        for (const data of [ami, amt]) {
          for (const group of (data?.groups ?? [])) {
            for (const order of group.orders) {
              for (const line of order.lines) {
                if (line.isNegative) continue;
                flat.push({
                  partyName:  group.partyName,
                  orderRef:   order.ref,
                  lpoDate:    order.date,
                  dueDate:    order.dueDate,
                  item:       line.item,
                  pendingQty: line.pendingQty,
                });
              }
            }
          }
        }
        flat.sort((a, b) => (a.dueDate || "9999") < (b.dueDate || "9999") ? -1 : 1);
        setRows(flat);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? rows.filter(r =>
      r.partyName.toLowerCase().includes(q) ||
      r.item.toLowerCase().includes(q) ||
      (r.orderRef || "").toLowerCase().includes(q)
    ) : rows;
  }, [rows, search]);

  return (
    <div className="card" style={{ position: "sticky", top: 12 }}>
      <div style={{
        padding: "9px 14px", borderBottom: "1px solid var(--color-border)",
        display: "flex", alignItems: "center", gap: 8,
      }}>
        <span style={{ fontWeight: 700, fontSize: 12 }}>Open Orders</span>
        <span style={{ fontSize: 11, color: "#6b7280" }}>{rows.length} lines</span>
        <input className="form-input" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)}
          style={{ marginLeft: "auto", width: 120, fontSize: 11, padding: "3px 7px" }} />
      </div>

      {loading && <div style={{ padding: 12, color: "#9ca3af", fontSize: 11 }}>Loading from Tally…</div>}
      {error   && <div className="alert-warn" style={{ margin: 10, fontSize: 11 }}>Error: {error}</div>}

      {!loading && !error && (
        <div style={{ overflowY: "auto", maxHeight: "calc(100vh - 200px)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #e2e8f0", background: "#f8fafc" }}>
                <th style={{ padding: "5px 10px", textAlign: "left", fontWeight: 600 }}>Customer</th>
                <th style={{ padding: "5px 10px", textAlign: "left", fontWeight: 600 }}>Grade</th>
                <th style={{ padding: "5px 10px", textAlign: "right", fontWeight: 600 }}>Qty</th>
                <th style={{ padding: "5px 10px", textAlign: "left", fontWeight: 600 }}>Due</th>
                <th style={{ padding: "5px 6px", width: 36 }}></th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: "center", color: "#9ca3af", padding: 20 }}>No open orders.</td></tr>
              )}
              {visible.map((r, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #f1f5f9", background: dueSoon(r.dueDate) ? "#fff7ed" : "white" }}>
                  <td style={{ padding: "5px 10px" }}>
                    <div style={{ fontWeight: 600, fontSize: 11 }}>{r.partyName}</div>
                    <div style={{ fontSize: 9, color: "#9ca3af", fontFamily: "monospace" }}>{r.orderRef}</div>
                  </td>
                  <td style={{ padding: "5px 10px", fontFamily: "monospace", fontWeight: 600, fontSize: 11 }}>{r.item}</td>
                  <td style={{ padding: "5px 10px", textAlign: "right", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{r.pendingQty}</td>
                  <td style={{ padding: "5px 10px", whiteSpace: "nowrap", color: dueSoon(r.dueDate) ? "#c2410c" : "#374151", fontWeight: dueSoon(r.dueDate) ? 700 : 400 }}>
                    {fmt(r.dueDate)}
                    {dueSoon(r.dueDate) && <span style={{ display: "block", fontSize: 9, color: "#c2410c" }}>SOON</span>}
                  </td>
                  <td style={{ padding: "5px 6px" }}>
                    <button
                      className="btn-small btn-primary"
                      style={{ fontSize: 10, padding: "2px 8px" }}
                      onClick={() => onRequestAdd({
                        partyName:  r.partyName,
                        lpoNo:      r.orderRef,
                        lpoDate:    r.lpoDate,
                        grade:      r.item,
                        quantity:   String(r.pendingQty || ""),
                        orderRef:   r.orderRef,
                      })}>
                      +
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Delivery Schedule (right column) ─────────────────────────────────────────

const EMPTY_LINE = {
  region: "Other", vehicle: "", partyName: "", delLocation: "",
  lpoNo: "", lpoDate: "", grade: "", packing: "", quantity: "",
  remarks: "", orderRef: "",
};

const inSx = {
  fontSize: 11, padding: "3px 5px", border: "1px solid #d1d5db",
  borderRadius: 3, background: "white", width: "100%",
};

function RegionSection({ region, entries, canEdit, onUpdateLine, onRemoveLine, onRenameRegion, onSetVehicle, onAddStop }) {
  const [collapsed, setCollapsed] = useState(false);

  const totalKg  = entries.reduce((s, { line }) => s + (parseFloat(line.quantity) || 0), 0);
  const vehicles = [...new Set(entries.map(({ line }) => line.vehicle).filter(Boolean))];
  const regionVehicle = vehicles[0] ?? "";

  return (
    <div style={{ marginBottom: 12, border: "1px solid #e2e8f0", borderRadius: 6, overflow: "hidden" }}>
      <div onClick={() => setCollapsed(c => !c)} style={{
        display: "flex", alignItems: "center", gap: 10, padding: "8px 12px",
        background: "#f8fafc", borderBottom: collapsed ? "none" : "1px solid #e2e8f0",
        cursor: "pointer", userSelect: "none",
      }}>
        <span style={{ fontSize: 10, color: "#9ca3af" }}>{collapsed ? "▸" : "▾"}</span>

        {canEdit ? (
          <select
            style={{ fontSize: 12, fontWeight: 700, border: "1px solid #d1d5db", borderRadius: 3, padding: "2px 6px", background: "white" }}
            value={region}
            onClick={e => e.stopPropagation()}
            onChange={e => onRenameRegion(region, e.target.value)}>
            {REGIONS.map(r => <option key={r}>{r}</option>)}
          </select>
        ) : (
          <span style={{ fontWeight: 700, fontSize: 12 }}>{region}</span>
        )}

        <span style={{ fontSize: 11, color: "#6b7280" }}>{entries.length} stop{entries.length !== 1 ? "s" : ""}</span>
        {totalKg > 0 && <span style={{ fontSize: 11, color: "#059669", fontWeight: 600 }}>{totalKg.toLocaleString()} KG</span>}

        {/* Vehicle field in header — updates all lines in this region */}
        {canEdit ? (
          <input
            style={{ ...inSx, width: 160, fontSize: 11, marginLeft: "auto" }}
            value={regionVehicle}
            placeholder="Vehicle / Driver"
            onClick={e => e.stopPropagation()}
            onChange={e => onSetVehicle(region, e.target.value)}
          />
        ) : (
          vehicles.length > 0 && (
            <span style={{ fontSize: 11, color: "#374151", marginLeft: "auto" }}>
              🚛 {vehicles.join(", ")}
            </span>
          )
        )}
      </div>

      {!collapsed && (
        <>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #e2e8f0", background: "#fafafa" }}>
                  <th style={{ padding: "4px 8px", width: 24, textAlign: "center" }}>#</th>
                  <th style={{ padding: "4px 8px", textAlign: "left" }}>Customer</th>
                  <th style={{ padding: "4px 8px", textAlign: "left" }}>LPO No</th>
                  <th style={{ padding: "4px 8px", textAlign: "left" }}>LPO Date</th>
                  <th style={{ padding: "4px 8px", textAlign: "left" }}>Grade</th>
                  <th style={{ padding: "4px 8px", textAlign: "left" }}>Packing</th>
                  <th style={{ padding: "4px 8px", textAlign: "right", width: 80 }}>Qty (KG)</th>
                  <th style={{ padding: "4px 8px", textAlign: "left" }}>Remarks</th>
                  {canEdit && <th style={{ width: 24 }}></th>}
                </tr>
              </thead>
              <tbody>
                {entries.map(({ line, idx }, si) => (
                  <tr key={idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "4px 8px", textAlign: "center", color: "#9ca3af", fontSize: 10 }}>{si + 1}</td>
                    {canEdit ? (
                      <>
                        <td style={{ padding: "4px 5px" }}><input style={inSx} value={line.partyName}   onChange={e => onUpdateLine(idx, "partyName",   e.target.value)} placeholder="Customer" /></td>
                        <td style={{ padding: "4px 5px" }}><input style={{ ...inSx, width: 100 }} value={line.lpoNo}      onChange={e => onUpdateLine(idx, "lpoNo",      e.target.value)} placeholder="LPO No" /></td>
                        <td style={{ padding: "4px 5px" }}><input style={{ ...inSx, width: 110 }} type="date" value={line.lpoDate}    onChange={e => onUpdateLine(idx, "lpoDate",    e.target.value)} /></td>
                        <td style={{ padding: "4px 5px" }}><input style={inSx} value={line.grade}      onChange={e => onUpdateLine(idx, "grade",      e.target.value)} placeholder="Grade" /></td>
                        <td style={{ padding: "4px 5px" }}><input style={{ ...inSx, width: 80 }} value={line.packing}    onChange={e => onUpdateLine(idx, "packing",    e.target.value)} placeholder="20KG" /></td>
                        <td style={{ padding: "4px 5px" }}><input style={{ ...inSx, width: 70, textAlign: "right" }} type="number" min="0" value={line.quantity}   onChange={e => onUpdateLine(idx, "quantity",   e.target.value)} placeholder="0" /></td>
                        <td style={{ padding: "4px 5px" }}><input style={inSx} value={line.remarks}    onChange={e => onUpdateLine(idx, "remarks",    e.target.value)} placeholder="Call basis" /></td>
                        <td style={{ padding: "4px 5px" }}>
                          <button onClick={() => onRemoveLine(idx)} style={{ background: "none", border: "none", color: "#dc2626", cursor: "pointer", fontSize: 13, lineHeight: 1 }}>✕</button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td style={{ padding: "4px 8px", fontWeight: 600 }}>{line.partyName || "—"}</td>
                        <td style={{ padding: "4px 8px", fontFamily: "monospace", fontSize: 10 }}>{line.lpoNo || "—"}</td>
                        <td style={{ padding: "4px 8px", color: "#6b7280" }}>{fmt(line.lpoDate)}</td>
                        <td style={{ padding: "4px 8px", fontFamily: "monospace", fontWeight: 600 }}>{line.grade || "—"}</td>
                        <td style={{ padding: "4px 8px" }}>{line.packing || "—"}</td>
                        <td style={{ padding: "4px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{line.quantity || "—"}</td>
                        <td style={{ padding: "4px 8px", color: "#6b7280" }}>{line.remarks || "—"}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {canEdit && (
            <div style={{ padding: "6px 12px" }}>
              <button className="btn" style={{ fontSize: 11, padding: "3px 10px" }} onClick={() => onAddStop(region)}>
                + Add Stop in {region}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function DeliveryScheduleSection({ addLineRef, myPerms, planDate, onPlanDateChange, planStatus, onStatusChange }) {
  const showToast = useToast();
  const canEdit     = myPerms?.includes("RM_EDIT")    && planStatus !== "FINAL";
  const canFinalize = myPerms?.includes("USER_MANAGE");

  const [lines,   setLines]   = useState([]);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);

  addLineRef.current = (lineData) => {
    setLines(prev => [...prev, { ...EMPTY_LINE, ...lineData }]);
  };

  useEffect(() => {
    setLoading(true); setSaved(false);
    api.fetchDeliveryPlan(planDate)
      .then(plan => {
        if (plan?.lines?.length) {
          setLines(plan.lines.map(l => ({
            region:      l.region       ?? "Other",
            vehicle:     l.vehicle      ?? "",
            partyName:   l.party_name   ?? "",
            delLocation: l.del_location ?? "",
            lpoNo:       l.lpo_no       ?? "",
            lpoDate:     l.lpo_date     ?? "",
            grade:       l.grade        ?? "",
            packing:     l.packing      ?? "",
            quantity:    l.quantity     ?? "",
            remarks:     l.remarks      ?? "",
            orderRef:    l.order_ref    ?? "",
          })));
          onStatusChange?.(plan.status ?? "DRAFT");
        } else {
          setLines([]);
          onStatusChange?.("DRAFT");
        }
      })
      .catch(() => { setLines([]); onStatusChange?.("DRAFT"); })
      .finally(() => setLoading(false));
  }, [planDate]);

  function updateLine(i, field, value) {
    setLines(prev => prev.map((l, idx) => idx === i ? { ...l, [field]: value } : l));
  }

  function removeLine(i) {
    setLines(prev => prev.filter((_, idx) => idx !== i));
  }

  // Rename all lines in a region to a new region name
  function renameRegion(oldRegion, newRegion) {
    setLines(prev => prev.map(l => (l.region || "Other") === oldRegion ? { ...l, region: newRegion } : l));
  }

  // Bulk-set vehicle for all lines in a region
  function setVehicleForRegion(region, vehicle) {
    setLines(prev => prev.map(l => (l.region || "Other") === region ? { ...l, vehicle } : l));
  }

  function addStop(region) {
    const existingVehicle = lines.find(l => (l.region || "Other") === region && l.vehicle)?.vehicle ?? "";
    setLines(prev => [...prev, { ...EMPTY_LINE, region, vehicle: existingVehicle }]);
  }

  function addRegion() {
    const usedRegions = new Set(lines.map(l => l.region || "Other"));
    const next = REGIONS.find(r => !usedRegions.has(r)) ?? "Other";
    setLines(prev => [...prev, { ...EMPTY_LINE, region: next }]);
  }

  async function handleSave() {
    setSaving(true);
    try {
      await api.saveDeliveryPlan({ planDate, lines: lines.filter(l => l.partyName.trim() || l.grade.trim()) });
      setSaved(true);
      onStatusChange?.("DRAFT");
      showToast("Delivery plan saved");
      setTimeout(() => setSaved(false), 3000);
    } finally {
      setSaving(false);
    }
  }

  async function handleSetStatus(status) {
    setWorking(true);
    try {
      await api.setDeliveryPlanStatus(planDate, status);
      onStatusChange?.(status);
      showToast(status === "FINAL" ? "Plan finalized" : "Plan reverted to draft");
    } catch (e) {
      alert("Failed: " + e.message);
    } finally {
      setWorking(false);
    }
  }

  // Group lines by region (preserve insertion order of first appearance)
  const regionOrder = useMemo(() => {
    const seen = new Set();
    const order = [];
    lines.forEach(l => {
      const r = l.region || "Other";
      if (!seen.has(r)) { seen.add(r); order.push(r); }
    });
    return order;
  }, [lines]);

  const totalStops = lines.filter(l => l.partyName || l.grade).length;
  const totalKg    = lines.reduce((s, l) => s + (parseFloat(l.quantity) || 0), 0);

  return (
    <div className="card">
      <div style={{
        padding: "9px 14px", borderBottom: "1px solid var(--color-border)",
        display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
      }}>
        <span style={{ fontWeight: 700, fontSize: 12 }}>Delivery Schedule</span>
        <input type="date" value={planDate} onChange={e => onPlanDateChange(e.target.value)}
          className="form-input" style={{ width: 140, fontSize: 11 }} />
        {loading && <span style={{ fontSize: 11, color: "#9ca3af" }}>Loading…</span>}
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          <StatusBadge status={planStatus} />
        </div>
      </div>

      <div style={{ padding: "12px 14px 6px" }}>
        {regionOrder.length === 0 && (
          <div style={{ textAlign: "center", color: "#9ca3af", padding: "20px 0", fontSize: 12 }}>
            No deliveries planned. Add from open orders or add a region below.
          </div>
        )}

        {regionOrder.map(region => {
          const entries = lines.reduce((acc, l, i) => {
            if ((l.region || "Other") === region) acc.push({ line: l, idx: i });
            return acc;
          }, []);
          return (
            <RegionSection
              key={region}
              region={region}
              entries={entries}
              canEdit={canEdit}
              onUpdateLine={updateLine}
              onRemoveLine={removeLine}
              onRenameRegion={renameRegion}
              onSetVehicle={setVehicleForRegion}
              onAddStop={addStop}
            />
          );
        })}

        {canEdit && (
          <button className="btn" style={{ fontSize: 11, padding: "4px 12px" }} onClick={addRegion}>
            + Add Region
          </button>
        )}
      </div>

      <div style={{
        padding: "9px 14px", borderTop: "1px solid var(--color-border)",
        display: "flex", alignItems: "center", gap: 8, background: "#fafafa", flexWrap: "wrap",
      }}>
        {canEdit && (
          <button className="btn btn-primary" onClick={handleSave} disabled={saving} style={{ fontSize: 12 }}>
            {saving ? "Saving…" : "Save Draft"}
          </button>
        )}
        {saved && <span style={{ fontSize: 11, color: "#15803d" }}>✓ Saved</span>}
        {canFinalize && planStatus === "DRAFT" && totalStops > 0 && (
          <button className="btn" disabled={working}
            style={{ fontSize: 11, background: "#dcfce7", color: "#166534", border: "1px solid #bbf7d0" }}
            onClick={() => handleSetStatus("FINAL")}>
            {working ? "…" : "✓ Finalize Plan"}
          </button>
        )}
        {canFinalize && planStatus === "FINAL" && (
          <button className="btn" disabled={working} style={{ fontSize: 11 }}
            onClick={() => handleSetStatus("DRAFT")}>
            {working ? "…" : "Revert to Draft"}
          </button>
        )}
        <span style={{ marginLeft: "auto", fontSize: 10, color: "#9ca3af" }}>
          {regionOrder.length} region{regionOrder.length !== 1 ? "s" : ""} · {totalStops} stop{totalStops !== 1 ? "s" : ""}
          {totalKg > 0 && ` · ${totalKg.toLocaleString()} KG`}
        </span>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function PageDeliveryPlan({ myPerms }) {
  const [planDate,   setPlanDate]   = useState(tomorrow());
  const [planStatus, setPlanStatus] = useState("DRAFT");
  const [addModal,   setAddModal]   = useState(null);
  const addLineRef = { current: null };

  function handleRequestAdd(lineData) {
    setAddModal({ lineData });
  }

  function handleConfirmAdd(region, vehicle) {
    addLineRef.current?.({ ...addModal.lineData, region, vehicle });
    setAddModal(null);
  }

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Delivery Planning</div>
          <div className="page-subtitle">Daily delivery schedule — grouped by region, assigned to vehicles</div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "340px 1fr", gap: 16, alignItems: "start" }}>
        <OpenOrdersSection onRequestAdd={handleRequestAdd} />
        <DeliveryScheduleSection
          addLineRef={addLineRef}
          myPerms={myPerms}
          planDate={planDate}
          onPlanDateChange={setPlanDate}
          planStatus={planStatus}
          onStatusChange={setPlanStatus}
        />
      </div>

      {addModal && (
        <AddToPlanModal
          lineData={addModal.lineData}
          existingLines={[]}
          onConfirm={handleConfirmAdd}
          onClose={() => setAddModal(null)}
        />
      )}
    </>
  );
}
